import { CommonModule, DecimalPipe } from '@angular/common'
import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core'
import { UntypedFormControl } from '@angular/forms'
import { ActivatedRoute } from '@angular/router'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { DashboardResponse } from '@app/modules/events/models/dashboard.model'
import { EventService } from '@app/modules/events/services/event.service'
import 'chartjs-plugin-labels'
import { Subject, takeUntil } from 'rxjs'
import { DashboardService } from '../../dashboard.service'

@Component({
  selector: 'ngx-gross-sales-card',
  templateUrl: './gross-sales-card.component.html',
  styleUrls: ['./gross-sales-card.component.scss'],
  imports: [CommonModule, DecimalPipe]
})
export class GrossSalesCardComponent implements OnInit, AfterViewInit {
  @ViewChild('grossSales') private chartRef: ElementRef
  @Input() dashboardData: DashboardResponse
  @Input() currencySlug: string
  netProfit: number
  processingFees: number
  commission: UntypedFormControl
  extraCommission: UntypedFormControl
  eventId: number
  isAdmin = false
  private readonly _destroy$ = new Subject()

  constructor(private _EventService: EventService, private _DashboardService: DashboardService, private _AuthService: AuthService, private _ActivatedRoute: ActivatedRoute) {}

  ngOnInit(): void {
    this.eventId = +this._ActivatedRoute.snapshot.params?.eventId
    this.commission = new UntypedFormControl(this.dashboardData?.commission ?? 0)
    this.extraCommission = new UntypedFormControl(this.dashboardData?.extra_commission_amount ?? 0)
    this.calculateNetProfitAndFees()
    this.isAdmin = this._AuthService.role === 'Admin'
  }

  ngAfterViewInit(): void {
    this.renderChart()
  }

  ngOnDestroy(): void {
    this._destroy$.next(null)
    this._destroy$.complete()
  }

  private calculateNetProfitAndFees(): void {
    const grossSales = this.dashboardData?.gross_sales
    this.processingFees = +(
      ((grossSales / this.dashboardData?.number_registration) * (this.commission.value / 100) + this.extraCommission.value) *
      this.dashboardData?.number_registration
    ).toFixed(2)
    this.netProfit = +(grossSales - this.processingFees).toFixed(2)
  }

  private renderChart(): void {
    const ctx = this.chartRef.nativeElement.getContext('2d')
    const netProfit = this.netProfit || 0
    const processingFees = this.processingFees || 0
    const netProfitLabel = `Net profit: ${netProfit} ${this.currencySlug?.toUpperCase()}`
    const processingFeesLabel = `Processing fees: ${processingFees} ${this.currencySlug?.toUpperCase()}`
    const options = {
      type: 'doughnut',
      labels: [netProfitLabel, processingFeesLabel],
      datasets: [
        {
          label: '# of Votes',
          data: [netProfit, processingFees],
          backgroundColor: ['rgba(28, 100, 242, 1)', 'rgba(253, 186, 140, 1)'],
          hoverBackgroundColor: ['rgba(28, 100, 242, 1)', 'rgba(253, 186, 140, 1)'],
          borderWidth: 1
        }
      ],
      chartOptions: {
        rotation: 1 * Math.PI,
        circumference: 1 * Math.PI,
        cutoutPercentage: 70,
        legend: {
          display: false
        },
        plugins: {
          labels: {
            render: 'percentage',
            fontColor: '#fff',
            precision: 0,
            fontSize: 10
          }
        },
        tooltips: {
          enabled: false
        }
      }
    }
    this._DashboardService.renderChart(ctx, this.dashboardData, options)
  }

  public updateCommissionValue(newCommission: number): void {
    if (!newCommission) {
      newCommission = 0
      this.commission.setValue(newCommission, { emitEvent: false })
    }
    this._EventService
      .updateCommissionValue(this.eventId, newCommission)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.calculateNetProfitAndFees()
        this.renderChart()
      })
  }

  public updateExtraCommissionValue(newExtraCommission: number): void {
    if (!newExtraCommission) {
      newExtraCommission = 0
      this.extraCommission.setValue(newExtraCommission, { emitEvent: false })
    }
    this._EventService
      .updateExtraCommissionValue(this.eventId, newExtraCommission)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.calculateNetProfitAndFees()
        this.renderChart()
      })
  }
}
